import { useMemo } from 'react';
import type Handsontable from 'handsontable';
import type { Dispatch, MutableRefObject, RefObject, SetStateAction } from 'react';
import { AIColumnActionType } from '../../AIColumnPopover';
import type { IMenuState } from '../components/TableContextMenu';
import { createNewColumn, sortTableData } from '../utils';
import type { IAIColumnPopoverState, IColumnConfig, TTableRow } from '../types';

interface IUseMenuActionsOptions {
  /** Handsontable 实例访问器 */
  getHotInstance: () => Handsontable | null;
  /** 当前菜单状态（仅用于派生 selectedIndices/cellCoords，hook 内部 useMemo 基于其变化重建） */
  contextMenu: IMenuState;
  /** 菜单状态 ref（弹出 AI 列操作时读取最新 x/y） */
  contextMenuRef: RefObject<IMenuState>;
  /** 关闭菜单 */
  closeContextMenu: () => void;
  /** 同步表格数据到 React state + 通知外部 */
  syncData: (changedColKey?: string) => void;
  /** 列结构变更入栈 + 应用新状态 */
  pushColumnAction: (
    prevData: TTableRow[],
    prevColumns: IColumnConfig[],
    nextData: TTableRow[],
    nextColumns: IColumnConfig[],
  ) => void;

  // 数据 ref（避免 hook 依赖频繁变化的 state）
  tableDataRef: RefObject<TTableRow[]>;
  columnsRef: RefObject<IColumnConfig[]>;
  onDataChangeRef: RefObject<((data: TTableRow[], columns: IColumnConfig[]) => void) | undefined>;
  /** 复制内容 ref（用于绕过焦点导致的剪贴板读取失败） */
  clipboardDataRef: MutableRefObject<{ text: string; html: string; }>;
  /** 内部编辑标记（用于阻止 props 回传引发的重复同步） */
  isInternalEditRef: MutableRefObject<boolean>;
  /** 直接 set 表格数据（排序场景） */
  setTableData: Dispatch<SetStateAction<TTableRow[]>>;
  /** AI 列操作弹层状态 setter */
  setAIColumnPopover: Dispatch<SetStateAction<IAIColumnPopoverState>>;
}

export interface IMenuActions {
  copy: () => void;
  paste: () => void;
  insertRowAbove: (count: number) => void;
  insertRowBelow: (count: number) => void;
  clearRowContent: () => void;
  deleteRow: () => void;
  sortAsc: () => void;
  sortDesc: () => void;
  insertColumnLeft: (count: number) => void;
  insertColumnRight: (count: number) => void;
  clearColumnContent: () => void;
  deleteColumn: () => void;
  aiModifyCondition: () => void;
  aiAdjustFormat: () => void;
  clearCellContent: () => void;
}

/**
 * 右键菜单动作集合 hook
 *
 * 把原先组件内的 menuActions useMemo 整体抽出。
 * 依赖项与原实现完全一致：仅 contextMenu.selectedIndices / cellCoords 变化时重建。
 * 所有跨数据访问通过传入的 *Ref 完成，避免 hook 内引用频繁变化的 React state。
 */
export const useMenuActions = (options: IUseMenuActionsOptions): IMenuActions => {
  const {
    getHotInstance,
    contextMenu,
    contextMenuRef,
    closeContextMenu,
    syncData,
    pushColumnAction,
    tableDataRef,
    columnsRef,
    onDataChangeRef,
    clipboardDataRef,
    isInternalEditRef,
    setTableData,
    setAIColumnPopover,
  } = options;

  return useMemo<IMenuActions>(() => {
    const hot = () => getHotInstance();
    const indices = contextMenu.selectedIndices;
    // 兼容：取第一个和最后一个索引，用于插入操作
    const firstIdx = indices.length > 0 ? Math.min(...indices) : -1;
    const lastIdx = indices.length > 0 ? Math.max(...indices) : -1;

    return {
      copy: () => {
        const h = hot();
        if (h) {
          const plugin = h.getPlugin('copyPaste') as any;
          // 获取当前选区范围
          plugin.setCopyableText();
          const ranges = plugin.copyableRanges;
          if (ranges && ranges.length > 0) {
            // 提取选区文本数据，保存到内部 ref
            const text = plugin.getRangedCopyableData(ranges);
            clipboardDataRef.current = { text, html: text };
            // 同时写入系统剪贴板
            navigator.clipboard?.writeText(text).catch(() => { /* 忽略剪贴板写入失败 */ });
          }
        }
        closeContextMenu();
      },
      paste: () => {
        const h = hot();
        if (h) {
          const { text, html } = clipboardDataRef.current;
          if (text) {
            // 确保 Handsontable 处于监听状态，否则 onPaste 内部会因 isListening()===false 而跳过
            h.listen();
            const plugin = h.getPlugin('copyPaste') as any;
            plugin.paste(text, html);
          }
        }
        closeContextMenu();
      },

      insertRowAbove: (count: number) => {
        if (firstIdx >= 0) { hot()?.alter('insert_row_above', firstIdx, count);syncData() }
        closeContextMenu();
      },
      insertRowBelow: (count: number) => {
        if (lastIdx >= 0) { hot()?.alter('insert_row_below', lastIdx, count);syncData() }
        closeContextMenu();
      },
      clearRowContent: () => {
        const h = hot();
        if (h && indices.length > 0) {
          const colCount = h.countCols();
          // 批量清空所有选中行
          const changes: [number, number, null][] = [];
          indices.forEach(rowIdx => {
            for (let col = 0;col < colCount;col++) changes.push([rowIdx, col, null]);
          });
          h.setDataAtCell(changes);
          syncData();
        }
        closeContextMenu();
      },
      deleteRow: () => {
        if (indices.length > 0) {
          const h = hot();
          // 从后往前删除，避免索引偏移
          const sorted = [...indices].sort((a, b) => b - a);
          sorted.forEach(rowIdx => { h?.alter('remove_row', rowIdx, 1) });
          syncData();
        }
        closeContextMenu();
      },

      sortAsc: () => {
        const cols = columnsRef.current ?? [];
        const data = tableDataRef.current ?? [];
        if (firstIdx >= 0 && cols[firstIdx]?.sortable !== false) {
          const columnKey = cols[firstIdx].data;
          const columnType = cols[firstIdx].type;
          // 第一行为表头行，不参与排序
          const headerRow = data[0];
          const dataRows = data.slice(1);
          const sortedDataRows = sortTableData(dataRows, columnKey, 'asc', columnType);
          const sortedData = [headerRow, ...sortedDataRows];
          isInternalEditRef.current = true;
          setTableData(sortedData);
          onDataChangeRef.current?.(sortedData, cols);
        }
        closeContextMenu();
      },
      sortDesc: () => {
        const cols = columnsRef.current ?? [];
        const data = tableDataRef.current ?? [];
        if (firstIdx >= 0 && cols[firstIdx]?.sortable !== false) {
          const columnKey = cols[firstIdx].data;
          const columnType = cols[firstIdx].type;
          // 第一行为表头行，不参与排序
          const headerRow = data[0];
          const dataRows = data.slice(1);
          const sortedDataRows = sortTableData(dataRows, columnKey, 'desc', columnType);
          const sortedData = [headerRow, ...sortedDataRows];
          isInternalEditRef.current = true;
          setTableData(sortedData);
          onDataChangeRef.current?.(sortedData, cols);
        }
        closeContextMenu();
      },

      insertColumnLeft: (count: number) => {
        const cols = columnsRef.current ?? [];
        const data = tableDataRef.current ?? [];
        if (firstIdx >= 0) {
          const newCols = [...cols];
          const newColConfigs: IColumnConfig[] = [];
          const baseId = Date.now();
          for (let i = 0;i < count;i++) {
            const col = createNewColumn(baseId, i);
            newCols.splice(firstIdx + i, 0, col);
            newColConfigs.push(col);
          }
          const newData = data.map(row => {
            const extended = { ...row };
            newColConfigs.forEach(col => { extended[col.data] = null });
            return extended;
          });
          pushColumnAction(data, cols, newData, newCols);
        }
        closeContextMenu();
      },
      insertColumnRight: (count: number) => {
        const cols = columnsRef.current ?? [];
        const data = tableDataRef.current ?? [];
        if (lastIdx >= 0) {
          const newCols = [...cols];
          const newColConfigs: IColumnConfig[] = [];
          const baseId = Date.now();
          for (let i = 0;i < count;i++) {
            const col = createNewColumn(baseId, i);
            newCols.splice(lastIdx + 1 + i, 0, col);
            newColConfigs.push(col);
          }
          const newData = data.map(row => {
            const extended = { ...row };
            newColConfigs.forEach(col => { extended[col.data] = null });
            return extended;
          });
          pushColumnAction(data, cols, newData, newCols);
        }
        closeContextMenu();
      },
      clearColumnContent: () => {
        const h = hot();
        if (h && indices.length > 0) {
          const rowCount = h.countRows();
          // 批量清空所有选中列
          const changes: [number, number, null][] = [];
          indices.forEach(colIdx => {
            for (let row = 0;row < rowCount;row++) {
              changes.push([row, colIdx, null]);
            }
          });
          h.setDataAtCell(changes);
          syncData();
        }
        closeContextMenu();
      },
      deleteColumn: () => {
        const cols = columnsRef.current ?? [];
        const data = tableDataRef.current ?? [];
        if (indices.length > 0) {
          // 批量删除选中的所有列
          const indicesToDelete = new Set(indices);
          const deletedColKeys = indices.map(i => cols[i]?.data).filter(Boolean);
          const newCols = cols.filter((_, i) => !indicesToDelete.has(i));
          const newData = data.map(row => {
            const newRow = { ...row };
            deletedColKeys.forEach(key => { delete newRow[key] });
            return newRow;
          });
          pushColumnAction(data, cols, newData, newCols);
          // 删除列后取消选区，避免残留高亮
          const h = hot();
          if (h) h.deselectCell();
        }
        closeContextMenu();
      },

      aiModifyCondition: () => {
        if (firstIdx >= 0) {
          // 显示 AI 列操作弹层（多选时只对第一列生效）
          const { x = 0, y = 0 } = contextMenuRef.current ?? {};
          setAIColumnPopover({
            visible: true,
            actionType: AIColumnActionType.MODIFY_CONDITION,
            position: { x, y },
            colIndex: firstIdx,
          });
        }
        closeContextMenu();
      },
      aiAdjustFormat: () => {
        if (firstIdx >= 0) {
          // 显示 AI 列操作弹层（多选时只对第一列生效）
          const { x = 0, y = 0 } = contextMenuRef.current ?? {};
          setAIColumnPopover({
            visible: true,
            actionType: AIColumnActionType.ADJUST_FORMAT,
            position: { x, y },
            colIndex: firstIdx,
          });
        }
        closeContextMenu();
      },

      clearCellContent: () => {
        const h = hot();
        const { cellCoords } = contextMenu;
        if (h && cellCoords) {
          h.setDataAtCell(cellCoords.row, cellCoords.col, null);
          syncData();
        }
        closeContextMenu();
      },
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getHotInstance, closeContextMenu, syncData, contextMenu.selectedIndices, contextMenu.cellCoords, pushColumnAction]);
};
